import _ from 'lodash';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrderAPI } from '../../apis/order-api';
import { useSuggestionAPI } from '../../apis/suggestion-api';
import { SubmitOrderModalBody } from '../../components/order/submit-order-modal-body';
import { useErrorHandler } from '../../hooks/use-error-handler';
import { useStorage } from '../../hooks/use-storage';
import { useSwal } from '../../hooks/use-swal';
import { parseSuggestions } from '../../utils/manager/suggestion-manager';
import { useAnalytics } from '../../utils/setup/analytics-setup';

function useSubmitOrderModal() {
  const {
    loadOrderFromRemote,
    findSpotOrdersFromRemote,
    submitOrderFromRemote,
  } = useOrderAPI();
  const { findSuggestionsFromRemote } = useSuggestionAPI();
  const { analyticsPageView, analyticsFeatureUseEvent } = useAnalytics();
  const { handleErrorOnModal } = useErrorHandler();

  const storage = useStorage();
  const { spotRef, setSession, menuCategories, setOrders } = storage;
  // const openWhatsApp = useOpenWhatsApp();
  const { swal } = useSwal({ storage });
  const suggestionRef = useRef(null);
  const suggestionsRef = useRef(null);
  const { t } = useTranslation();

  const newOrderRef = useRef();

  function didOpen() {
    if (spotRef.current?.prepaid) {
      analyticsPageView('Order Submit');
    } else {
      analyticsPageView('Order Sent');
    }
  }

  const handleClick = (suggestion) => {
    analyticsFeatureUseEvent('ordering suggestion click');
    suggestionRef.current = suggestion;
    swal.clickConfirm();
  };

  async function prepare({ newOrder }) {
    newOrderRef.current = await submit(newOrder);

    _.delay(async () => {
      const response = await findSpotOrdersFromRemote();
      setOrders(response.data);
    }, 0);

    const lSession = newOrderRef.current?.session;
    setSession(lSession);

    const response = await findSuggestionsFromRemote({ session: lSession });

    if (_.isArray(response.data)) {
      const itemIds = response.data?.map((s) => s.item.id);
      suggestionsRef.current = parseSuggestions(menuCategories, itemIds);

      console.log('suggestions', suggestionsRef.current);
    }
  }

  async function submit(newOrder) {
    // const newOrder = newOrderRef.current;
    const spot = spotRef.current;

    const data = {
      item: { id: newOrder.item.id },
      price: { id: newOrder.price.id },
      options: newOrder.options
        ?.filter((option) => !option.blocked)
        ?.map((option) => ({ id: option.id })),
      quantity: newOrder.quantity,
      spot: { id: spot.id },
      note: _.isEmpty(newOrder.note) ? null : newOrder.note,
      unique: newOrder.unique,
    };

    let conflictId = null;
    let response = await submitOrderFromRemote(data).catch((error) => {
      if (
        error.response?.status === 409 &&
        error.response?.data?.find((v) => v.code === 99) != null
      ) {
        conflictId = error.response.headers['conflict-id'];
      } else {
        throw error;
      }
    });

    if (conflictId != null) {
      response = await loadOrderFromRemote(conflictId);
    }

    return response?.data;
  }

  async function show({ prepareOnPreConfirm } = {}) {
    suggestionRef.current = null;
    const prepaid = spotRef.current?.prepaid;

    const result = await swal.fire({
      title: prepaid
        ? t('order-sent-modal.not-forget-send')
        : t('order-sent-modal.sent'),
      text: ' ',
      icon: prepaid ? 'warning' : 'success',
      didOpen,
      showConfirmButton: true,
      showLoaderOnConfirm: true,
      // showDenyButton: showContactButton,
      showCancelButton: true,
      confirmButtonText: prepaid
        ? t('order-sent-modal.send-order')
        : t('order-sent-modal.bill-and-pay'),
      showCloseButton: true,
      allowOutsideClick: true,
      cancelButtonText: prepaid
        ? t('order-sent-modal.keep-choosing')
        : t('continue'),
      footer: !_.isEmpty(suggestionsRef.current) && (
        <SubmitOrderModalBody
          analyticsFeatureUseEvent={analyticsFeatureUseEvent}
          storage={storage}
          suggestions={suggestionsRef.current}
          spot={spotRef.current}
          onClick={handleClick}
        />
      ),
      preConfirm: () => preConfirm({ prepareOnPreConfirm }),
    });

    result.newOrder = newOrderRef.current;
    result.suggestion = suggestionRef.current;

    return result;
  }

  async function preConfirm({ prepareOnPreConfirm }) {
    try {
      await prepareOnPreConfirm?.call(this);
    } catch (error) {
      handleErrorOnModal(error);
      return false;
    }

    return true;
  }

  return {
    prepareSubmitOrderModal: prepare,
    showSubmitOrderModal: show,
  };
}

export { useSubmitOrderModal };
